'use client';

import React from 'react';
import ReactDatepicker, { type DatePickerProps } from 'react-datepicker';
import { flip, shift, type Middleware } from '@floating-ui/react';
import PortalWrp from '@creditflowresearch/uilib/Layout/PortalWrp';
import { TIME_FORMAT_12, YEAR_MONTH_DAY_SLASH_FORMAT } from '@creditflowresearch/uilib/utils/dateFormats';
import { OVERFLOW_PADDING } from './constants';

import styles from './baseDatePicker.module.scss';

export type IBaseDatePickerProps = DatePickerProps & {
  dataTest: string,
  datePickerRef?: React.Ref<ReactDatepicker>,
  usePortal?: boolean,
  isPreventOverflowEnabled?: boolean,
  isFlipEnabled?: boolean,
};

const PortalWrapper = ({ children }: { children?: React.ReactNode }) => (
  <PortalWrp>
    <div className={ styles.reactDatepickerPortalEncapsulationGlobalStyles }>
      { children }
    </div>
  </PortalWrp>
);

const BaseDatePicker = ({
  dateFormat = YEAR_MONTH_DAY_SLASH_FORMAT,
  showTimeSelectOnly,
  datePickerRef,
  dataTest,
  usePortal,
  isPreventOverflowEnabled = true,
  isFlipEnabled = true,
  popperModifiers = [],
  ...otherProps
}: IBaseDatePickerProps) => {
  const mergedPopperModifiers = [
    isPreventOverflowEnabled && shift({ padding: OVERFLOW_PADDING, boundary: document.body }),
    isFlipEnabled ? flip() : {
      name: 'resetPlacement',
      fn: (state) => ({ reset: { placement: state.initialPlacement } }),
    },
    ...popperModifiers,
  ].filter(Boolean) as Middleware[];

  return (
    <div className={ styles.reactDatepickerEncapsulationGlobalStyles }>
      <ReactDatepicker
        dateFormat={ showTimeSelectOnly ? TIME_FORMAT_12 : dateFormat }
        popperPlacement="bottom-end"
        className={ styles.reactDatepicker }
        popperContainer={ usePortal ? PortalWrapper : undefined }
        popperModifiers={ mergedPopperModifiers }
        data-test={ dataTest }
        showTimeSelectOnly={ showTimeSelectOnly }
        { ...otherProps }
        // ref must stay after otherProps so it is never overwritten by a spread ref
        ref={ datePickerRef }
      />
    </div>
  );
};

export default BaseDatePicker;
